import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  UseWizardResult,
  WizardConfig,
  WizardError,
  WizardStepId,
  WizardStepState,
  WizardStepStatus,
} from '../components/wizard/wizard.types';

/**
 * A custom hook to manage wizard state and navigation
 */
export function useWizard<T = Record<string, unknown>>(
  config: WizardConfig<T>
): UseWizardResult<T> {
  const { steps = [], defaultStep, onComplete, stepProps = {} as T } = config;

  // Core minimal state - only track what truly needs to be stateful
  const [currentId, setCurrentId] = useState(() => defaultStep || steps[0]?.id || '');
  const [statusMap, setStatusMap] = useState<Record<WizardStepId, WizardStepStatus>>(() => {
    const initial: Record<WizardStepId, WizardStepStatus> = {};
    steps.forEach(step => {
      initial[step.id] = step.id === currentId ? 'current' : step.initialStatus || 'pending';
    });
    return initial;
  });

  // Global error state instead of per-step messages
  const [error, setError] = useState<WizardError>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle dynamic steps (adding and removing)
  useEffect(() => {
    setStatusMap(prevStatusMap => {
      // Get current step index based on the current steps array
      const curIndex = steps.findIndex(s => s.id === currentId);

      // New status map to return
      const newStatusMap = { ...prevStatusMap };
      let hasChanges = false;

      // Handle new steps
      steps.forEach(step => {
        // If step is new (not in statusMap)
        if (prevStatusMap[step.id] === undefined) {
          newStatusMap[step.id] = step.initialStatus || 'pending';
          hasChanges = true;
        }
      });

      // Handle removed steps
      const existingStepIds = new Set(steps.map(step => step.id));
      Object.keys(prevStatusMap).forEach(id => {
        if (!existingStepIds.has(id)) {
          delete newStatusMap[id];
          hasChanges = true;
        }
      });

      // Always ensure current step has correct status
      if (currentId && existingStepIds.has(currentId) && newStatusMap[currentId] !== 'current') {
        newStatusMap[currentId] = 'current';
        hasChanges = true;
      }

      // For steps before current, ensure proper status
      steps.forEach((step, index) => {
        if (index < curIndex) {
          // Only update if not already completed or visited
          const currentStatus = newStatusMap[step.id] || 'pending';
          if (!['completed', 'visited'].includes(currentStatus)) {
            newStatusMap[step.id] = 'completed';
            hasChanges = true;
          }
        }
      });

      // Only return new object if changes were made
      return hasChanges ? newStatusMap : prevStatusMap;
    });

    // Handle case where current step was removed
    if (currentId && !steps.some(s => s.id === currentId) && steps.length > 0) {
      setCurrentId(steps[0].id);
    }
  }, [steps, currentId]);

  // Computed properties
  const currentIndex = useMemo(() => steps.findIndex(s => s.id === currentId), [steps, currentId]);
  const currentStep = useMemo(() => steps.find(s => s.id === currentId), [steps, currentId]);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;
  const hasError = statusMap[currentId] === 'error';

  // Unified step states (now without messages)
  const stepStates = useMemo(() => {
    const states: Record<WizardStepId, WizardStepState> = {};
    steps.forEach(step => {
      states[step.id] = {
        status: statusMap[step.id] || 'pending',
      };
    });
    return states;
  }, [steps, statusMap]);

  // Reset function to restore initial state
  const reset = useCallback(() => {
    const startId = defaultStep || steps[0]?.id || '';

    const newStatusMap: Record<WizardStepId, WizardStepStatus> = {};
    steps.forEach(step => {
      newStatusMap[step.id] = step.id === startId ? 'current' : step.initialStatus || 'pending';
    });

    setStatusMap(newStatusMap);
    setCurrentId(startId);
    setError(null);
    setIsLoading(false);
  }, [steps, defaultStep]);

  // Simplified status setters
  const setStatus = useCallback((id: WizardStepId, status: WizardStepStatus) => {
    setStatusMap(prev => ({ ...prev, [id]: status }));
  }, []);

  // Error handling
  const setErrorState = useCallback(
    (newError: WizardError) => {
      setError(newError);
      if (newError) {
        setStatus(currentId, 'error');
      }
    },
    [currentId, setStatus]
  );

  const clearError = useCallback(() => {
    setError(null);
    if (statusMap[currentId] === 'error') {
      setStatus(currentId, 'current');
    }
  }, [currentId, statusMap, setStatus]);

  // Go to a specific step
  const goTo = useCallback(
    async (targetId: WizardStepId) => {
      // Handle empty steps array or invalid target
      if (steps.length === 0 || !steps.some(s => s.id === targetId)) {
        return false;
      }

      // Check if we can leave current step
      if (currentStep?.onBeforeExit) {
        try {
          const canExit = await Promise.resolve(currentStep.onBeforeExit());
          if (!canExit) return false;
        } catch (error) {
          setErrorState(
            typeof error === 'string'
              ? error
              : {
                  title: 'Error',
                  message: error instanceof Error ? error.message : String(error),
                }
          );
          return false;
        }
      }

      // Check if we can enter target step
      const targetStep = steps.find(s => s.id === targetId);
      if (targetStep?.onBeforeEnter) {
        try {
          setIsLoading(true);
          const canEnter = await Promise.resolve(targetStep.onBeforeEnter());
          if (!canEnter) {
            setIsLoading(false);
            return false;
          }
        } catch (error) {
          setErrorState(
            typeof error === 'string'
              ? error
              : {
                  title: 'Error',
                  message: error instanceof Error ? error.message : String(error),
                }
          );
          setIsLoading(false);
          return false;
        }
      }

      // Update current step and status
      setCurrentId(targetId);
      setStatus(targetId, 'current');
      setIsLoading(false);
      return true;
    },
    [currentStep, setErrorState, steps, setStatus]
  );

  // Create navigation object with UI state
  const navigation = useMemo(
    () => ({
      // Navigation methods
      next: async () => {
        if (isLastStep || steps.length === 0) return false;
        clearError();
        setStatus(currentId, 'loading');
        const result = await goTo(steps[currentIndex + 1].id);
        if (result) {
          setStatus(currentId, 'completed');
        }
        return result;
      },

      skip: async () => {
        if (isLastStep || steps.length === 0) return false;
        clearError();
        setStatus(currentId, 'visited');
        return goTo(steps[currentIndex + 1].id);
      },

      prev: async () => {
        if (isFirstStep || steps.length === 0) return false;
        clearError();
        return goTo(steps[currentIndex - 1].id);
      },

      complete: async () => {
        clearError();
        setStatus(currentId, 'completed');

        if (onComplete) {
          try {
            setIsLoading(true);
            await Promise.resolve(onComplete());
            // Reset wizard after successful completion
            reset();
          } catch (error) {
            setErrorState(
              typeof error === 'string'
                ? error
                : {
                    title: 'Error',
                    message: error instanceof Error ? error.message : String(error),
                  }
            );
            setIsLoading(false);
            return false;
          }
        } else {
          // If no onComplete handler, still reset
          reset();
        }

        setIsLoading(false);
        return true;
      },

      goTo,

      // UI state for navigation components
      isFirstStep,
      isLastStep,
      isLoading,
      hasError,
      setLoading: setIsLoading,
    }),
    [
      isLastStep,
      isFirstStep,
      clearError,
      setStatus,
      currentId,
      currentIndex,
      goTo,
      steps,
      onComplete,
      setErrorState,
      isLoading,
      hasError,
      setIsLoading,
      reset,
    ]
  );

  return {
    // Core state
    steps,
    currentStep,
    currentId,
    currentIndex,
    stepStates,

    // Error handling
    error,
    setError: setErrorState,
    clearError,

    // Status handling
    setStatus,
    setLoading: setIsLoading,

    // Navigation with UI state
    navigation,

    // Custom props
    stepProps: stepProps as T,

    // Reset functionality
    reset,
  };
}
